import { Color, IconType } from 'constants/assets'
import { FC, Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react'

import Button from 'components/common/Button/Button'
import Dropdown from 'components/common/Dropdown/Dropdown'
import Icon from 'components/common/Icon/Icon'
import { PreferredPaymentMethodEnum } from 'constants/user'
import { PreferredPaymentMethodType } from 'models/user/preferredPaymentMethod'
import ReactLoading from 'react-loading'
import styles from './PreferredPaymentMethodSelect.module.sass'
import { useAvailablePaymentMethods } from '../_main/contexts'
import { useTranslation } from 'react-i18next'
import { useUpdatePreferredPaymentMethod } from 'dataQueries'
import { useUserData } from 'components/contexts/UserDataContext'

interface Props {
  /** Class name to be added to main wrapper */
  className?: string
}

/**
 * @component Displays editable select prefilled with saved preferred payment method. Method can be changed and saved with included button.
 * @example
 *  <PreferredPaymentMethodSelect />
 */
export const PreferredPaymentMethodSelect: FC<Props> = ({
  className,
}) => {

  const { t } = useTranslation(['preferred_payment_method', 'product_kind'])

  const { clientData } = useUserData()
  const { availablePaymentMethods } = useAvailablePaymentMethods()
  const updateMethod = useUpdatePreferredPaymentMethod()

  // Append artificial NONE payment method
  const userAvailablePaymentMethods = useMemo(() => [...availablePaymentMethods, PreferredPaymentMethodEnum.NONE], [availablePaymentMethods])

  const userPreferredMethod = useMemo(() => clientData?.preferredPaymentMethod || PreferredPaymentMethodEnum.NONE, [clientData])

  const [isPrefilled, setIsPrefilled] = useState<boolean>(false)
  const [preferredMethod, setPreferredMethod] = useState<PreferredPaymentMethodType>(PreferredPaymentMethodEnum.NONE)

  const handleValueChange = useCallback((method: PreferredPaymentMethodType) => {
    if (updateMethod.isSuccess || updateMethod.isError) updateMethod.reset()
    setPreferredMethod(method)
  }, [updateMethod])

  // Prefill select with saved method
  useLayoutEffect(() => {
    if (!isPrefilled && userPreferredMethod) {
      setPreferredMethod(userPreferredMethod)
      setIsPrefilled(true)
    }
  }, [isPrefilled, userPreferredMethod])

  // Reset mutation state on destroy
  useEffect(() => {
    return () => {
      updateMethod.reset()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className={`${styles.preferredPaymentMethodSelect} ${className}`}>

      <label htmlFor="preferred-payment-method-select">
        {t('label')}
      </label>

      <div className={styles.selectWrapper}>

        <Dropdown button={(isOpen, action) => (
          <Button type="secondary" onClick={action} textAndIcon={true}>
            <span>{t(`product_kind:${preferredMethod}`)}</span>
            <Icon icon={IconType.CARET_DOWN} className={`caret ${isOpen ? 'up' : 'down'}`} />
          </Button>
        )}>
          <Fragment>
            {userAvailablePaymentMethods.map(method => {
              const selected = method === preferredMethod
              return (
                <button
                  key={method.toString()}
                  className={selected ? 'selected' : ''}
                  onClick={() => handleValueChange(method)}
                >
                  {t(`product_kind:${method}`)}
                </button>
              )
            })}
          </Fragment>
        </Dropdown>

        {userPreferredMethod !== preferredMethod && !updateMethod.isPending &&
          <Button
            type="secondary"
            className={styles.saveButton}
            onClick={() => updateMethod.mutate({ paymentMethod: preferredMethod })}>
            {t('save')}
          </Button>
        }

        {updateMethod.isPending &&
          <ReactLoading type="spin" className={styles.loading} color={Color.GRAY_SECONDARY} />
        }

        {updateMethod.isSuccess &&
          <Icon icon={IconType.CHECK} className={styles.successIcon} color={Color.SECONDARY_GREEN_DARK} />
        }

        {updateMethod.isError &&
          <Icon icon={IconType.CROSS} className={styles.successIcon} color={Color.SECONDARY_RED_DARK} />
        }

      </div>

      {updateMethod.isError &&
        <p className={styles.error}>
          {t('error')}
        </p>
      }

    </div>
  )
}
